import { apiClient } from '@/lib/api'
import {
  type KeycloakUser,
  type KeycloakRole,
  type CreateUserRequest,
  type CreateUserResponse,
  type UpdateUserRequest,
  type UpdateAttributesRequest,
  type AssignRolesRequest,
  type ResetPasswordRequest,
} from '@/models/domain/keycloak-user'

export interface ListUsersParams {
  search?: string
  offset?: number
  limit?: number
}

class UserService {
  async listUsers(params?: ListUsersParams): Promise<KeycloakUser[]> {
    const allUsers: KeycloakUser[] = []
    let offset = 0
    const batchSize = 100
    const maxBatches = 10

    let batchCount = 0
    while (batchCount < maxBatches) {
      const queryParams: Record<string, string> = {
        offset: String(offset),
        limit: String(batchSize),
      }

      if (params?.search) {
        queryParams.search = params.search
      }

      try {
        const batch = await apiClient.get<KeycloakUser[]>('/core/v5/admin/users', queryParams)

        if (batch.length === 0) {
          break
        }

        allUsers.push(...batch)

        if (batch.length < batchSize) {
          break
        }

        offset += batchSize
        batchCount++
      } catch (error) {
        console.error('[UserService] Error fetching users batch:', error)
        if (allUsers.length > 0) {
          return allUsers
        }
        throw error
      }
    }

    return allUsers
  }

  async getUser(id: string): Promise<KeycloakUser> {
    return await apiClient.get<KeycloakUser>(`/core/v5/admin/users/${id}`)
  }

  async createUser(request: CreateUserRequest): Promise<CreateUserResponse> {
    return await apiClient.post<CreateUserResponse>('/core/v5/admin/users', request)
  }

  async updateUser(id: string, request: UpdateUserRequest): Promise<void> {
    await apiClient.put(`/core/v5/admin/users/${id}`, request)
  }

  async getUserRoles(id: string): Promise<KeycloakRole[]> {
    return await apiClient.get<KeycloakRole[]>(`/core/v5/admin/users/${id}/roles`)
  }

  async assignRoles(id: string, roles: KeycloakRole[]): Promise<void> {
    const request: AssignRolesRequest = { roles }
    await apiClient.post(`/core/v5/admin/users/${id}/roles`, request)
  }

  async removeRoles(id: string, roles: KeycloakRole[]): Promise<void> {
    const request: AssignRolesRequest = { roles }
    await apiClient.delete(`/core/v5/admin/users/${id}/roles`, request)
  }

  async updateAttributes(id: string, attributes: UpdateAttributesRequest): Promise<void> {
    await apiClient.put(`/core/v5/admin/users/${id}/attributes`, attributes)
  }

  async resetPassword(id: string, password: string, temporary: boolean): Promise<void> {
    const request: ResetPasswordRequest = {
      type: 'password',
      value: password,
      temporary,
    }
    await apiClient.put(`/core/v5/admin/users/${id}/password`, request)
  }

  async logoutUser(id: string): Promise<void> {
    await apiClient.post(`/core/v5/admin/users/${id}/logout`, {})
  }

  async getRealmRoles(): Promise<KeycloakRole[]> {
    return await apiClient.get<KeycloakRole[]>('/core/v5/admin/roles')
  }
}

export const userService = new UserService()
